/**
 * Error Monitoring Service
 *
 * Comprehensive error tracking, logging, and reporting system for LC Performance.
 * Monitors JavaScript errors, promise rejections, console errors, and network failures.
 *
 * Features:
 * - Automatic error capture
 * - Console error tracking
 * - Network failure detection
 * - Performance issue detection
 * - Error categorization
 * - Bug report generation
 * - Local storage logging
 * - Development vs production modes
 *
 * @module errorMonitor
 */

/**
 * Error severity levels
 */
export const ErrorSeverity = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low'
};

/**
 * Error types
 */
export const ErrorType = {
  JAVASCRIPT: 'JavaScript',
  NETWORK: 'Network',
  REACT: 'React',
  PROMISE: 'Promise',
  PERFORMANCE: 'Performance',
  ACCESSIBILITY: 'Accessibility',
  VALIDATION: 'Validation'
};

/**
 * Error Monitor Service
 */
class ErrorMonitor {
  constructor() {
    this.errors = [];
    this.maxErrors = 100; // Keep last 100 errors
    this.listeners = [];
    this.isInitialized = false;
    this.isDevelopment = import.meta.env.DEV;
    this.storageKey = 'lc_performance_errors';
  }

  /**
   * Initialize error monitoring
   * Sets up global error handlers
   */
  init() {
    if (this.isInitialized) {
      console.warn('[ErrorMonitor] Already initialized');
      return;
    }

    // Load previous errors from storage
    this.loadErrorsFromStorage();

    // Global error handler
    window.addEventListener('error', (event) => {
      this.handleError({
        type: ErrorType.JAVASCRIPT,
        severity: ErrorSeverity.HIGH,
        message: event.message,
        filename: event.filename,
        line: event.lineno,
        column: event.colno,
        stack: event.error?.stack,
        timestamp: new Date().toISOString()
      });
    });

    // Unhandled promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.handleError({
        type: ErrorType.PROMISE,
        severity: ErrorSeverity.HIGH,
        message: event.reason?.message || 'Unhandled Promise Rejection',
        stack: event.reason?.stack,
        timestamp: new Date().toISOString()
      });
    });

    // Override console.error to track errors
    this.interceptConsoleError();

    // Monitor performance issues
    this.monitorPerformance();

    this.isInitialized = true;
    console.log('[ErrorMonitor] Initialized successfully');
  }

  /**
   * Intercept console.error to track errors
   */
  interceptConsoleError() {
    const originalError = console.error;
    console.error = (...args) => {
      // Call original console.error
      originalError.apply(console, args);

      // Track the error
      const message = args.map(arg =>
        typeof arg === 'object' ? JSON.stringify(arg) : String(arg)
      ).join(' ');

      this.handleError({
        type: ErrorType.JAVASCRIPT,
        severity: ErrorSeverity.MEDIUM,
        message,
        timestamp: new Date().toISOString()
      });
    };
  }

  /**
   * Monitor performance issues
   */
  monitorPerformance() {
    // Monitor long tasks (>50ms blocking)
    if ('PerformanceObserver' in window) {
      try {
        const observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 50) {
              this.handleError({
                type: ErrorType.PERFORMANCE,
                severity: ErrorSeverity.MEDIUM,
                message: `Long task detected: ${entry.duration.toFixed(2)}ms`,
                details: {
                  name: entry.name,
                  duration: entry.duration,
                  startTime: entry.startTime
                },
                timestamp: new Date().toISOString()
              });
            }
          }
        });
        observer.observe({ entryTypes: ['longtask'] });
      } catch (e) {
        // Long task API not supported
      }
    }

    // Monitor page load performance
    window.addEventListener('load', () => {
      setTimeout(() => {
        const perfData = window.performance.timing;
        const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;

        if (pageLoadTime > 3000) {
          this.handleError({
            type: ErrorType.PERFORMANCE,
            severity: ErrorSeverity.MEDIUM,
            message: `Slow page load: ${pageLoadTime}ms`,
            details: {
              pageLoadTime,
              domContentLoaded: perfData.domContentLoadedEventEnd - perfData.navigationStart,
              domInteractive: perfData.domInteractive - perfData.navigationStart
            },
            timestamp: new Date().toISOString()
          });
        }
      }, 0);
    });
  }

  /**
   * Handle and log an error
   * @param {Object} error - Error object
   */
  handleError(error) {
    // Add unique ID
    error.id = this.generateErrorId();

    // Add to errors array
    this.errors.unshift(error);

    // Limit array size
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    // Save to storage
    this.saveErrorsToStorage();

    // Notify listeners
    this.notifyListeners(error);

    // Log to console in development
    if (this.isDevelopment) {
      console.group(`[ErrorMonitor] ${error.severity} - ${error.type}`);
      console.log('Message:', error.message);
      if (error.stack) console.log('Stack:', error.stack);
      if (error.details) console.log('Details:', error.details);
      console.groupEnd();
    }
  }

  /**
   * Track a network error
   * @param {Object} error - Network error details
   */
  trackNetworkError(error) {
    this.handleError({
      type: ErrorType.NETWORK,
      severity: this.getNetworkErrorSeverity(error.status),
      message: error.message || 'Network request failed',
      details: {
        url: error.url,
        status: error.status,
        statusText: error.statusText,
        method: error.method
      },
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track a React component error
   * @param {Error} error - Error object
   * @param {Object} errorInfo - Error info from React
   */
  trackReactError(error, errorInfo) {
    this.handleError({
      type: ErrorType.REACT,
      severity: ErrorSeverity.CRITICAL,
      message: error.message,
      stack: error.stack,
      details: {
        componentStack: errorInfo.componentStack
      },
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track a validation error
   * @param {Object} error - Validation error details
   */
  trackValidationError(error) {
    this.handleError({
      type: ErrorType.VALIDATION,
      severity: ErrorSeverity.LOW,
      message: error.message || 'Validation failed',
      details: error.details,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Track an accessibility issue
   * @param {Object} issue - Accessibility issue details
   */
  trackAccessibilityIssue(issue) {
    this.handleError({
      type: ErrorType.ACCESSIBILITY,
      severity: ErrorSeverity.MEDIUM,
      message: issue.message,
      details: issue.details,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Get network error severity based on status code
   * @param {number} status - HTTP status code
   * @returns {string} Severity level
   */
  getNetworkErrorSeverity(status) {
    if (status >= 500) return ErrorSeverity.CRITICAL;
    if (status === 404) return ErrorSeverity.MEDIUM;
    if (status >= 400) return ErrorSeverity.HIGH;
    return ErrorSeverity.LOW;
  }

  /**
   * Generate unique error ID
   * @returns {string} Error ID
   */
  generateErrorId() {
    return `ERR-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Get all errors
   * @param {Object} filters - Filter options
   * @returns {Array} Filtered errors
   */
  getErrors(filters = {}) {
    let filtered = [...this.errors];

    if (filters.type) {
      filtered = filtered.filter(e => e.type === filters.type);
    }

    if (filters.severity) {
      filtered = filtered.filter(e => e.severity === filters.severity);
    }

    if (filters.since) {
      const sinceDate = new Date(filters.since);
      filtered = filtered.filter(e => new Date(e.timestamp) >= sinceDate);
    }

    return filtered;
  }

  /**
   * Get error statistics
   * @returns {Object} Error statistics
   */
  getStats() {
    const stats = {
      total: this.errors.length,
      bySeverity: {},
      byType: {},
      last24Hours: 0,
      lastError: this.errors[0] || null
    };

    // Count by severity
    Object.values(ErrorSeverity).forEach(severity => {
      stats.bySeverity[severity] = this.errors.filter(e => e.severity === severity).length;
    });

    // Count by type
    Object.values(ErrorType).forEach(type => {
      stats.byType[type] = this.errors.filter(e => e.type === type).length;
    });

    // Count last 24 hours
    const last24Hours = new Date(Date.now() - 24 * 60 * 60 * 1000);
    stats.last24Hours = this.errors.filter(e =>
      new Date(e.timestamp) >= last24Hours
    ).length;

    return stats;
  }

  /**
   * Generate bug report
   * @param {string} errorId - Error ID
   * @returns {string} Bug report markdown
   */
  generateBugReport(errorId) {
    const error = this.errors.find(e => e.id === errorId);
    if (!error) return null;

    const bugNumber = this.errors.indexOf(error) + 1;
    const date = new Date(error.timestamp).toLocaleString();

    return `## Bug #${String(bugNumber).padStart(3, '0')}: ${error.message}

**Reported**: ${date}
**Severity**: ${error.severity}
**Type**: ${error.type}
**Status**: Open

### Description
${error.message}

### Error Details
${error.stack ? '```\n' + error.stack + '\n```' : 'No stack trace available'}

${error.details ? '### Additional Details\n```json\n' + JSON.stringify(error.details, null, 2) + '\n```' : ''}

### Environment
- Browser: ${navigator.userAgent}
- Screen Size: ${window.innerWidth}x${window.innerHeight}
- URL: ${window.location.href}
- Timestamp: ${error.timestamp}

### Code Location
${error.filename ? `- File: ${error.filename}` : ''}
${error.line ? `- Line: ${error.line}:${error.column}` : ''}

### Next Steps
1. Reproduce the error
2. Identify root cause
3. Implement fix
4. Test across browsers
5. Verify resolution
`;
  }

  /**
   * Export all errors as JSON
   * @returns {string} JSON string
   */
  exportErrors() {
    return JSON.stringify({
      exported: new Date().toISOString(),
      stats: this.getStats(),
      errors: this.errors
    }, null, 2);
  }

  /**
   * Clear all errors
   */
  clearErrors() {
    this.errors = [];
    this.saveErrorsToStorage();
    console.log('[ErrorMonitor] All errors cleared');
  }

  /**
   * Save errors to localStorage
   */
  saveErrorsToStorage() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.errors));
    } catch (e) {
      console.warn('[ErrorMonitor] Could not save to localStorage:', e);
    }
  }

  /**
   * Load errors from localStorage
   */
  loadErrorsFromStorage() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        this.errors = JSON.parse(stored);
        console.log(`[ErrorMonitor] Loaded ${this.errors.length} errors from storage`);
      }
    } catch (e) {
      console.warn('[ErrorMonitor] Could not load from localStorage:', e);
    }
  }

  /**
   * Subscribe to error events
   * @param {Function} callback - Callback function
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }

  /**
   * Notify all listeners of new error
   * @param {Object} error - Error object
   */
  notifyListeners(error) {
    this.listeners.forEach(callback => {
      try {
        callback(error);
      } catch (e) {
        console.error('[ErrorMonitor] Error in listener:', e);
      }
    });
  }

  /**
   * Get health status
   * @returns {Object} Health status
   */
  getHealthStatus() {
    const stats = this.getStats();
    const criticalErrors = stats.bySeverity[ErrorSeverity.CRITICAL] || 0;
    const highErrors = stats.bySeverity[ErrorSeverity.HIGH] || 0;

    let status = 'healthy';
    let message = 'All systems operational';

    if (criticalErrors > 0) {
      status = 'critical';
      message = `${criticalErrors} critical error(s) detected`;
    } else if (highErrors > 5) {
      status = 'warning';
      message = `${highErrors} high-severity errors detected`;
    } else if (stats.total > 20) {
      status = 'warning';
      message = `${stats.total} errors logged`;
    }

    return {
      status,
      message,
      stats,
      timestamp: new Date().toISOString()
    };
  }
}

// Create singleton instance
const errorMonitor = new ErrorMonitor();

// Auto-initialize in production
if (!import.meta.env.DEV) {
  errorMonitor.init();
}

export default errorMonitor;

/**
 * React hook for error monitoring
 */
export function useErrorMonitor() {
  const [errors, setErrors] = React.useState([]);
  const [stats, setStats] = React.useState(null);

  React.useEffect(() => {
    setErrors(errorMonitor.getErrors());
    setStats(errorMonitor.getStats());

    const unsubscribe = errorMonitor.subscribe(() => {
      setErrors(errorMonitor.getErrors());
      setStats(errorMonitor.getStats());
    });

    return unsubscribe;
  }, []);

  return {
    errors,
    stats,
    trackError: (error) => errorMonitor.handleError(error),
    trackNetworkError: (error) => errorMonitor.trackNetworkError(error),
    trackReactError: (error, info) => errorMonitor.trackReactError(error, info),
    clearErrors: () => errorMonitor.clearErrors(),
    exportErrors: () => errorMonitor.exportErrors(),
    getHealthStatus: () => errorMonitor.getHealthStatus()
  };
}
